import { z } from "zod";

/**
 * Banner status enum
 */
export enum EBannerStatus {
  ACTIVE = "ACTIVE",
  DRAFT = "DRAFT",
}

/**
 * Banner validation schema
 */
export const bannerSchema = z.object({
  id: z.string(),
  imageUrl: z.string()
    .url("Image URL must be valid")
    .min(10, "Image URL must be at least 10 characters")
    .max(500, "Image URL must be less than 500 characters")
    .refine((url) => url.startsWith("http://") || url.startsWith("https://"), "Image URL must start with http:// or https://"),
  title: z.string()
    .min(3, "Title must be at least 3 characters")
    .max(100, "Title must be less than 100 characters")
    .regex(/^[a-zA-Z0-9\s\-_.,!?]+$/, "Title can only contain alphanumeric characters, spaces, and basic punctuation (-_.,!?)"),
  destinationUrl: z.string()
    .url("Destination URL must be valid")
    .min(10, "Destination URL must be at least 10 characters")
    .max(500, "Destination URL must be less than 500 characters")
    .refine((url) => url.startsWith("http://") || url.startsWith("https://"), "Destination URL must start with http:// or https://"),
  isDeepLink: z.boolean(),
  priority: z.number().min(1, "Priority must be at least 1"),
  status: z.nativeEnum(EBannerStatus),
  createdAt: z.number(),
  updatedAt: z.number(),
});

/**
 * Banner category validation schema
 */
export const bannerCategorySchema = z.object({
  id: z.string(),
  type: z.string()
    .min(3, "Type must be at least 3 characters")
    .max(50, "Type must be less than 50 characters")
    .regex(/^[a-zA-Z0-9\s-]+$/, "Type can only contain alphanumeric characters, spaces, and hyphens"),
  code: z.string()
    .min(3, "Code must be at least 3 characters")
    .max(20, "Code must be less than 20 characters")
    .regex(/^[A-Z0-9-]+$/, "Code must be uppercase letters, numbers, and hyphens only"),
  banners: z.array(bannerSchema)
    .min(1, "Category must have at least 1 banner")
    .max(20, "Category can have at most 20 banners"),
  createdAt: z.number(),
  updatedAt: z.number(),
});

/**
 * Banner interface
 */
export interface IBanner {
  id: string;
  imageUrl: string;
  title: string;
  destinationUrl: string;
  isDeepLink: boolean;
  priority: number;
  status: EBannerStatus;
  createdAt: number;
  updatedAt: number;
}

/**
 * Banner category interface
 */
export interface IBannerCategory {
  id: string;
  type: string;
  code: string;
  banners: IBanner[];
  createdAt: number;
  updatedAt: number;
}

/**
 * Banner form data interface for creating/editing banners
 */
export interface IBannerFormData {
  imageUrl: string;
  title: string;
  destinationUrl: string;
  isDeepLink: boolean;
  priority: number;
  status: EBannerStatus;
}

/**
 * Banner category form data interface for creating categories
 */
export interface IBannerCategoryFormData {
  type: string;
  code: string;
  banners: IBannerFormData[];
}

/**
 * Banner API response interface
 */
export interface IBannerApiResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data: IBanner[];
}

/**
 * Banner category API response interface
 */
export interface IBannerCategoryApiResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data: IBannerCategory[];
}

/**
 * Banner form validation schema
 */
export const bannerFormSchema = z.object({
  imageUrl: z.string()
    .url("Image URL must be valid")
    .min(10, "Image URL must be at least 10 characters")
    .max(500, "Image URL must be less than 500 characters")
    .refine((url) => url.startsWith("http://") || url.startsWith("https://"), "Image URL must start with http:// or https://"),
  title: z.string()
    .min(3, "Title must be at least 3 characters")
    .max(100, "Title must be less than 100 characters")
    .regex(/^[a-zA-Z0-9\s._,!?-]+$/, "Title can only contain alphanumeric characters, spaces, and basic punctuation (-_.,!?)"),
  destinationUrl: z.string()
    .url("Destination URL must be valid")
    .min(10, "Destination URL must be at least 10 characters")
    .max(500, "Destination URL must be less than 500 characters")
    .refine((url) => url.startsWith("http://") || url.startsWith("https://"), "Destination URL must start with http:// or https://"),
  isDeepLink: z.boolean(),
  priority: z.number().min(1, "Priority must be at least 1"),
  status: z.nativeEnum(EBannerStatus),
});

/**
 * Banner category form validation schema
 */
export const bannerCategoryFormSchema = z.object({
  type: z.string()
    .min(3, "Type must be at least 3 characters")
    .max(50, "Type must be less than 50 characters")
    .regex(/^[a-zA-Z0-9\s-]+$/, "Type can only contain alphanumeric characters, spaces, and hyphens"),
  code: z.string()
    .min(3, "Code must be at least 3 characters")
    .max(20, "Code must be less than 20 characters")
    .regex(/^[A-Z0-9-]+$/, "Code must be uppercase letters, numbers, and hyphens only"),
  banners: z.array(bannerFormSchema)
    .min(1, "Category must have at least 1 banner")
    .max(20, "Category can have at most 20 banners"),
});

/**
 * Banner drag and drop interface for priority updates
 */
export interface IBannerDragData {
  bannerId: string;
  categoryId: string;
  oldPriority: number;
  newPriority: number;
}

/**
 * Banner priority update interface
 */
export interface IBannerPriorityUpdate {
  bannerId: string;
  priority: number;
}

/**
 * Update banner priorities request interface
 */
export interface IUpdateBannerPrioritiesRequest {
  priorityUpdates: IBannerPriorityUpdate[];
}

/**
 * Banner priority item interface for drag and drop operations
 */
export interface IBannerPriorityItem {
  bannerId: string;
  title: string;
  priority: number;
  status: EBannerStatus;
}

/**
 * Banner priority form validation schema
 */
export const bannerPrioritySchema = z.object({
  banners: z.array(z.object({
    bannerId: z.string(),
    title: z.string(),
    priority: z.number().min(1, "Priority must be at least 1"),
    status: z.nativeEnum(EBannerStatus),
  }))
});

/**
 * Banner priority form data type
 */
export type BannerPriorityFormData = z.infer<typeof bannerPrioritySchema>;